import { computed, Injectable, signal } from '@angular/core';
import { IResolvedTheme, ITenantInfo, ITenantTheme, IThemeLinks, ThemeMode, ThemePreference } from 'app/models/theme';

// --- helpers: color math ---
function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const m = hex.trim().replace('#', '');
    const v =
        m.length === 3
            ? m
                  .split('')
                  .map((x) => x + x)
                  .join('')
            : m.padStart(6, '0').slice(0, 6);

    const num = parseInt(v, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    const d = max - min;

    if (d !== 0) {
        s = d / (1 - Math.abs(2 * l - 1));

        switch (max) {
            case r:
                h = 60 * (((g - b) / d) % 6);
                break;
            case g:
                h = 60 * ((b - r) / d + 2);
                break;
            case b:
                h = 60 * ((r - g) / d + 4);
                break;
        }
    }

    if (h < 0) h += 360;

    return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0;
    let g = 0;
    let b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else {
        r = c;
        g = 0;
        b = x;
    }

    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    };
}

function onColor(hex: string): string {
    const { r, g, b } = hexToRgb(hex);

    const toLinear = (v: number) => {
        const c = v / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);

    const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;

    const contrastWhite = 1.05 / (L + 0.05);
    const contrastBlack = (L + 0.05) / 0.05;

    return contrastBlack >= contrastWhite ? '#000000' : '#FFFFFF';
}

function makeScale(hex: string): Record<number, string> {
    const { r, g, b } = hexToRgb(hex);
    const { h, s, l } = rgbToHsl(r, g, b);

    const steps: Array<[number, number, number]> = [
        [50, Math.min(1, s * 0.6), 0.97],
        [100, Math.min(1, s * 0.65), 0.93],
        [200, Math.min(1, s * 0.75), 0.82],
        [300, Math.min(1, s * 0.85), 0.7],
        [400, Math.min(1, s * 0.95), 0.6],
        [500, s, l],
        [600, Math.min(1, s * 1.05), Math.max(0, l * 0.42)],
        [700, Math.min(1, s * 1.1), Math.max(0, l * 0.32)],
        [800, Math.min(1, s * 1.15), Math.max(0, l * 0.24)],
        [900, Math.min(1, s * 1.2), Math.max(0, l * 0.16)],
    ];

    const out: Record<number, string> = {};
    for (const [k, ss, ll] of steps) {
        const { r: rr, g: gg, b: bb } = hslToRgb(h, ss, ll);
        out[k] = `${rr} ${gg} ${bb}`;
    }

    return out;
}

interface INormalizedThemeTokens {
    key: string;
    mode: ThemeMode;

    primary: string;
    onPrimary: string;
    secondary: string;
    onSecondary: string;
    tertiary: string;
    onTertiary: string;
    error: string;
    onError: string;

    bg: string;
    surface: string;
    onSurface: string;

    surfaceContainer: string;
    surfaceContainerHigh: string;
    surfaceContainerHighest: string;

    panel: string;
    card: string;
    cardForeground: string;

    border: string;
    outline: string;
    outlineVariant: string;

    muted: string;
    mutedForeground: string;
    ring: string;

    logo?: string;
    favicon?: string;
    title?: string;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private mediaQueryList?: MediaQueryList;
    private removeSystemThemeListener?: () => void;

    private _tenantTheme = signal<ITenantTheme | null>(null);
    private _themePreference = signal<ThemePreference>('system');

    readonly activeTenantTheme = this._tenantTheme.asReadonly();
    readonly themePreference = this._themePreference.asReadonly();

    readonly resolvedMode = computed<ThemeMode>(() => {
        const tenant = this._tenantTheme();
        const pref = this._themePreference();
        return this.resolveMode(pref, tenant?.defaultMode);
    });

    readonly resolvedTheme = computed<IResolvedTheme | null>(() => {
        const tenant = this._tenantTheme();
        if (!tenant) return null;
        return this.resolveTheme(tenant, this.resolvedMode());
    });

    readonly logoUrl = computed(() => {
        const t = this.resolvedTheme();
        if (!t?.assets) return undefined;

        if (t.mode === 'dark' && t.assets.logoDark) {
            return t.assets.logoDark;
        }

        return t.assets.logo;
    });

    readonly links = computed<IThemeLinks | undefined>(() => this.resolvedTheme()?.links);
    readonly tenant = computed<ITenantInfo | undefined>(() => this.resolvedTheme()?.tenant);
    readonly layout = computed(() => this.resolvedTheme()?.assets?.layout);

    init(theme: ITenantTheme, preference?: ThemePreference): void {
        this._tenantTheme.set(theme);

        const storedPreference = this.readStoredThemePreference();
        const initialPreference = preference ?? storedPreference ?? theme.defaultMode ?? 'light';

        this._themePreference.set(initialPreference);

        this.reapplyResolvedTheme();
        this.bindSystemPreferenceListener();
    }

    setTenantTheme(theme: ITenantTheme): void {
        this._tenantTheme.set(theme);
        localStorage.setItem('themeKey', theme.key);
        this.reapplyResolvedTheme();
    }

    setThemePreference(preference: ThemePreference): void {
        this._themePreference.set(preference);
        localStorage.setItem('themePreference', preference);
        this.reapplyResolvedTheme();
    }

    toggleMode(): void {
        const next = this.resolvedMode() === 'dark' ? 'light' : 'dark';
        this.setThemePreference(next);
    }

    resolveMode(preference: ThemePreference, tenantDefault: ThemeMode = 'light'): ThemeMode {
        if (preference === 'light' || preference === 'dark') {
            return preference;
        }

        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }

        return tenantDefault;
    }

    resolveTheme(tenantTheme: ITenantTheme, mode: ThemeMode): IResolvedTheme {
        const scheme = tenantTheme.schemes[mode];

        return {
            key: tenantTheme.key,
            mode,
            assets: tenantTheme.assets,
            links: tenantTheme.links,
            tenant: tenantTheme.tenant,
            ...scheme,
        };
    }

    applyResolvedTheme(theme: IResolvedTheme): void {
        if (typeof document === 'undefined') return;

        const normalized = this.normalizeTheme(theme);
        this.applyThemeToDom(normalized);
    }

    private normalizeTheme(theme: IResolvedTheme): INormalizedThemeTokens {
        const surface = theme.surface;
        const onSurface = theme.onSurface;

        const surfaceContainer = theme.surfaceContainer ?? surface;
        const surfaceContainerHigh = theme.surfaceContainerHigh ?? surfaceContainer;
        const surfaceContainerHighest = theme.surfaceContainerHighest ?? surfaceContainerHigh;

        const panel = theme.panel ?? surfaceContainer;
        const card = theme.card ?? surfaceContainerHigh;
        const cardForeground = theme.cardForeground ?? onSurface;

        const outline = theme.outline ?? theme.border ?? onSurface;
        const outlineVariant = theme.outlineVariant ?? theme.border ?? outline;
        const border = theme.border ?? outlineVariant;

        const muted = theme.muted ?? surfaceContainer;
        const mutedForeground = theme.mutedForeground ?? onSurface;
        const ring = theme.ring ?? theme.primary;

        const onPrimary = onColor(theme.primary);
        const onSecondary = onColor(theme.secondary);
        const onTertiary = onColor(theme.tertiary);
        const onError = onColor(theme.error);

        const logo = theme.mode === 'dark' && theme.assets?.logoDark ? theme.assets.logoDark : theme.assets?.logo;

        const favicon = theme.mode === 'dark' && theme.assets?.faviconDark ? theme.assets.faviconDark : theme.assets?.favicon;

        return {
            key: theme.key,
            mode: theme.mode,

            primary: theme.primary,
            onPrimary,
            secondary: theme.secondary,
            onSecondary,
            tertiary: theme.tertiary,
            onTertiary,
            error: theme.error,
            onError,

            bg: theme.bg ?? surface,
            surface,
            onSurface,

            surfaceContainer,
            surfaceContainerHigh,
            surfaceContainerHighest,

            panel,
            card,
            cardForeground,

            border,
            outline,
            outlineVariant,

            muted,
            mutedForeground,
            ring,

            logo,
            favicon,
            title: theme.tenant?.title || 'Lead UI',
        };
    }

    private applyThemeToDom(theme: INormalizedThemeTokens): void {
        const root = document.documentElement;

        root.classList.toggle('dark', theme.mode === 'dark');
        root.setAttribute('data-theme', `${theme.key}-${theme.mode}`);

        // Fuse palette compatibility
        this.applyFusePalette('--fuse-primary', theme.primary);
        this.applyFusePalette('--fuse-secondary', theme.secondary);
        this.applyFusePalette('--fuse-tertiary', theme.tertiary);
        this.applyFusePalette('--fuse-error', theme.error);

        // Hex semantic tokens
        this.setHexVar(root, '--color-primary', theme.primary);
        this.setHexVar(root, '--color-on-primary', theme.onPrimary);
        this.setHexVar(root, '--color-secondary', theme.secondary);
        this.setHexVar(root, '--color-on-secondary', theme.onSecondary);
        this.setHexVar(root, '--color-tertiary', theme.tertiary);
        this.setHexVar(root, '--color-on-tertiary', theme.onTertiary);
        this.setHexVar(root, '--color-error', theme.error);
        this.setHexVar(root, '--color-on-error', theme.onError);

        this.setHexVar(root, '--color-bg', theme.bg);
        this.setHexVar(root, '--color-surface', theme.surface);
        this.setHexVar(root, '--color-on-surface', theme.onSurface);

        this.setHexVar(root, '--color-surface-container', theme.surfaceContainer);
        this.setHexVar(root, '--color-surface-container-high', theme.surfaceContainerHigh);
        this.setHexVar(root, '--color-surface-container-highest', theme.surfaceContainerHighest);

        this.setHexVar(root, '--color-panel', theme.panel);
        this.setHexVar(root, '--color-card', theme.card);
        this.setHexVar(root, '--color-card-foreground', theme.cardForeground);

        this.setHexVar(root, '--color-border', theme.border);
        this.setHexVar(root, '--color-outline', theme.outline);
        this.setHexVar(root, '--color-outline-variant', theme.outlineVariant);

        this.setHexVar(root, '--color-muted', theme.muted);
        this.setHexVar(root, '--color-muted-foreground', theme.mutedForeground);
        this.setHexVar(root, '--color-ring', theme.ring);

        // RGB triplet tokens for Tailwind / alpha usage
        this.setRgbVar(root, '--primary', theme.primary);
        this.setRgbVar(root, '--secondary', theme.secondary);
        this.setRgbVar(root, '--tertiary', theme.tertiary);
        this.setRgbVar(root, '--error', theme.error);

        this.setRgbVar(root, '--bg', theme.bg);
        this.setRgbVar(root, '--surface', theme.surface);
        this.setRgbVar(root, '--surface-container', theme.surfaceContainer);
        this.setRgbVar(root, '--surface-container-high', theme.surfaceContainerHigh);
        this.setRgbVar(root, '--surface-container-highest', theme.surfaceContainerHighest);

        this.setRgbVar(root, '--panel', theme.panel);
        this.setRgbVar(root, '--card', theme.card);
        this.setRgbVar(root, '--card-foreground', theme.cardForeground);

        this.setRgbVar(root, '--border', theme.border);
        this.setRgbVar(root, '--muted', theme.muted);
        this.setRgbVar(root, '--muted-foreground', theme.mutedForeground);
        this.setRgbVar(root, '--ring', theme.ring);
        this.setRgbVar(root, '--text', theme.onSurface);

        this.setRgbVar(root, '--outline', theme.outline);
        this.setRgbVar(root, '--outline-variant', theme.outlineVariant);

        // Existing compatibility tokens
        this.setHexVar(root, '--fuse-surface', theme.surface);
        this.setHexVar(root, '--fuse-on-surface', theme.onSurface);

        this.setHexVar(root, '--primary-hex', theme.primary);
        this.setHexVar(root, '--secondary-hex', theme.secondary);
        this.setHexVar(root, '--tertiary-hex', theme.tertiary);
        this.setHexVar(root, '--error-hex', theme.error);
        this.setHexVar(root, '--surface-hex', theme.surface);
        this.setHexVar(root, '--on-surface-hex', theme.onSurface);

        this.setRgbVar(root, '--brand', theme.primary);
        this.setRgbVar(root, '--surface-rgb', theme.surface);
        this.setRgbVar(root, '--brand-contrast', theme.onPrimary);

        // Angular Material / MDC bridge
        this.applyMaterialTokens(root, theme);

        document.title = theme.title ?? 'Lead UI';
        this.swapFavicon(theme.favicon);
    }

    private applyMaterialTokens(root: HTMLElement, theme: INormalizedThemeTokens): void {
        // Angular Material system tokens
        this.setHexVar(root, '--mat-sys-on-primary', theme.onPrimary);
        this.setHexVar(root, '--mat-sys-on-secondary', theme.onSecondary);
        this.setHexVar(root, '--mat-sys-on-tertiary', theme.onTertiary);
        this.setHexVar(root, '--mat-sys-on-error', theme.onError);
    }

    private reapplyResolvedTheme(): void {
        const resolved = this.resolvedTheme();
        if (resolved) {
            this.applyResolvedTheme(resolved);
        }
    }

    private bindSystemPreferenceListener(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        this.removeSystemThemeListener?.();

        this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

        const handler = () => {
            if (this.themePreference() !== 'system') {
                return;
            }

            this.reapplyResolvedTheme();
        };

        if (this.mediaQueryList.addEventListener) {
            this.mediaQueryList.addEventListener('change', handler);
            this.removeSystemThemeListener = () => {
                this.mediaQueryList?.removeEventListener('change', handler);
            };
            return;
        }

        this.mediaQueryList.addListener(handler);
        this.removeSystemThemeListener = () => {
            this.mediaQueryList?.removeListener(handler);
        };
    }

    private readStoredThemePreference(): ThemePreference | null {
        if (typeof localStorage === 'undefined') return null;

        const value = localStorage.getItem('themePreference');
        return this.isThemePreference(value) ? value : null;
    }

    private isThemePreference(value: unknown): value is ThemePreference {
        return value === 'light' || value === 'dark' || value === 'system';
    }

    private setHexVar(root: HTMLElement, name: string, value?: string): void {
        if (!value) return;
        root.style.setProperty(name, value);
    }

    private setRgbVar(root: HTMLElement, name: string, hex?: string): void {
        if (!hex) return;
        root.style.setProperty(name, this.hexToRgbTokens(hex));
    }

    private applyFusePalette(prefix: string, baseHex: string): void {
        if (typeof document === 'undefined') return;

        const root = document.documentElement;
        const body = document.body ?? document.documentElement;
        const scale = makeScale(baseHex);

        const levels = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

        for (const k of levels) {
            const rgb = scale[k];
            const rgbVar = `${prefix}-${k}-rgb`;

            root.style.setProperty(rgbVar, rgb);
            body.style.setProperty(rgbVar, rgb);
        }

        const defaultRgb = scale[500];
        const rgbShortVar = `${prefix}-rgb`;

        root.style.setProperty(rgbShortVar, defaultRgb);
        body.style.setProperty(rgbShortVar, defaultRgb);

        root.style.setProperty(prefix, baseHex);
        body.style.setProperty(prefix, baseHex);

        const on = onColor(baseHex);
        const onVar = `--${prefix.slice(2)}-on`;

        root.style.setProperty(onVar, on);
        body.style.setProperty(onVar, on);
    }

    private swapFavicon(url?: string): void {
        if (typeof document === 'undefined' || !url) return;

        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }

        link.href = url;
    }

    private hexToRgbTokens(hex: string): string {
        if (!hex) return '0 0 0';

        let h = hex.trim();
        if (h.startsWith('#')) h = h.slice(1);

        if (h.length === 3) {
            h = h
                .split('')
                .map((ch) => ch + ch)
                .join('');
        }

        if (h.length !== 6) return '0 0 0';

        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);

        return `${r} ${g} ${b}`;
    }
}
